import { formatDuration, SUBMISSION_STATUSES, type SubmissionStatus } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Bdi } from "../../components/Bdi.js";
import { useT } from "../../i18n/LocaleProvider.js";
import { api } from "../../lib/api.js";

interface Row {
  id: string;
  kind: string;
  story_title: string | null;
  identity_preference: string;
  display_name: string | null;
  has_email: boolean;
  locale: string;
  status: SubmissionStatus;
  spam_flagged: boolean;
  created_at: string;
  film_license: boolean | null;
  web_excerpts: boolean | null;
  future_contact: boolean | null;
  media_status: string | null;
  duration_seconds: string | null;
}

interface ListResponse {
  submissions: Row[];
  counts: Record<string, number>;
  quarantine: number;
}

export function AdminQueue() {
  const { m, lp, locale } = useT();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const spam = params.get("spam") === "1";
  const [data, setData] = useState<ListResponse | null>(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (spam) q.set("spam", "1");
    api.get<ListResponse>(`/api/admin/submissions?${q}`).then(setData).catch(() => setData({ submissions: [], counts: {}, quarantine: 0 }));
  }, [status, spam]);

  return (
    <>
      <div className="admin__tabs" role="tablist">
        <button type="button" className="admin__tab" aria-pressed={!status && !spam} onClick={() => setParams({})}>
          {m.admin.queue} ({Object.values(data?.counts ?? {}).reduce((a, b) => a + b, 0)})
        </button>
        {SUBMISSION_STATUSES.map((s) => (
          <button key={s} type="button" className="admin__tab" aria-pressed={status === s} onClick={() => setParams({ status: s })}>
            {m.admin.statuses[s]} ({data?.counts[s] ?? 0})
          </button>
        ))}
        <button type="button" className="admin__tab" aria-pressed={spam} onClick={() => setParams({ spam: "1" })} style={{ marginInlineStart: "auto" }}>
          {m.admin.quarantine} ({data?.quarantine ?? 0})
        </button>
      </div>
      {!data ? (
        <p className="muted">{m.common.loading}…</p>
      ) : data.submissions.length === 0 ? (
        <p className="muted">{m.admin.empty}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{m.admin.colStory}</th>
                <th>{m.admin.colIdentity}</th>
                <th>{m.admin.colConsent}</th>
                <th>{m.admin.status}</th>
                <th>{m.nav.language}</th>
                <th>{m.admin.colReceived}</th>
              </tr>
            </thead>
            <tbody>
              {data.submissions.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={lp(`/admin/submissions/${r.id}`)}>
                      <strong>{m.share.formats[r.kind as keyof typeof m.share.formats]?.title ?? r.kind}</strong>
                      {r.story_title && (
                        <>
                          {" · "}
                          <Bdi>{r.story_title}</Bdi>
                        </>
                      )}
                    </Link>
                    {r.duration_seconds && <div className="muted small">{formatDuration(Number(r.duration_seconds), locale)}</div>}
                    {r.media_status && r.media_status !== "ready" && <div className="badge badge--warn">{r.media_status}</div>}
                    {r.spam_flagged && <div className="badge badge--status">{m.admin.spamFlag}</div>}
                  </td>
                  <td>
                    {r.identity_preference === "anonymous" ? <span className="badge">{m.admin.anonymous}</span> : <Bdi>{r.display_name}</Bdi>}
                    {r.identity_preference === "pseudonym" && <div className="muted small">{m.admin.pseudonym}</div>}
                    {r.has_email && <div className="muted small">✉</div>}
                  </td>
                  <td>
                    <div className="flags">
                      <span className={`badge ${r.film_license ? "badge--ok" : ""}`}>{m.admin.consentFilm}: {r.film_license ? m.admin.granted : m.admin.notGranted}</span>
                      {r.web_excerpts && <span className="badge badge--ok">{m.admin.consentWeb}</span>}
                      {r.future_contact && <span className="badge badge--ok">{m.admin.consentContact}</span>}
                    </div>
                  </td>
                  <td>
                    <span className="badge">{m.admin.statuses[r.status]}</span>
                  </td>
                  <td>{r.locale}</td>
                  <td className="muted small">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
